/**
 * Tenant business profile (Settings › Company): who the business is, as opposed to how its documents look.
 *
 * Three separate concerns, never merged: branding (display name, logo), legal and fiscal identity (legal
 * name, tax registration number) and company-wide contact (phone, address). `Tenant.name` is the
 * system identity and is not edited here; the resolved display name is `displayName ?? Tenant.name`.
 * Printed documents do not read any of this yet: that integration is a later, explicit decision.
 */

/** Longest value each field accepts, in characters. */
export const BUSINESS_PROFILE_MAX_LENGTH = {
  displayName: 160,
  legalName: 200,
  taxRegistrationNumber: 50,
  businessPhone: 32,
  businessAddress: 500,
} as const;

/** An address may break lines, at most this many. */
export const BUSINESS_ADDRESS_MAX_LINES = 4;

/** Letters and digits in any script, plus the separators registration numbers are printed with: the supplier tax-number rule, no country format assumed. */
export const TAX_REGISTRATION_NUMBER_PATTERN = /^[\p{L}\p{N}\s\-./]+$/u;
/** Digits in any script, plus the separators people type: the supplier phone rule. */
export const BUSINESS_PHONE_PATTERN = /^[\d+\s()\-.٠-٩۰-۹]+$/u;

/** What a logo upload may be, by its decoded content. SVG is not accepted. */
export const TENANT_LOGO_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export interface TenantBusinessProfileFields {
  displayName: string | null;
  legalName: string | null;
  taxRegistrationNumber: string | null;
  businessPhone: string | null;
  businessAddress: string | null;
}

export type TenantBusinessProfileField = keyof TenantBusinessProfileFields;

/** A logo as a client may see it: short-lived signed URLs and dimensions, never a storage key. */
export interface TenantLogoView {
  url: string;
  thumbUrl: string;
  width: number;
  height: number;
}

export interface TenantBusinessProfileView extends TenantBusinessProfileFields {
  /** `Tenant.name`, the system identity; read-only here. */
  systemName: string;
  /** `displayName ?? systemName`. */
  resolvedDisplayName: string;
  logo: TenantLogoView | null;
  /** The version a save must name; 0 until the profile is first saved. */
  version: number;
}

export interface SaveTenantBusinessProfileRequest extends TenantBusinessProfileFields {
  version: number;
}

/** The seam a later print integration will consume: resolved on the server, so no renderer ever reads branding storage. */
export interface ResolvedBusinessIdentity {
  displayName: string;
  legalName: string | null;
  taxRegistrationNumber: string | null;
  phone: string | null;
  address: string | null;
  logoUrl: string | null;
}
